/**
 * Action menu for scenario mode.
 *
 * On desktop, renders the shared `DesktopDropdownMenu` (SE-5570 — on the
 * FixedPositionDropdownShell, replacing the retired gluestack Menu atom). On
 * mobile, renders the canonical MobileActionMenu bottom sheet (SE-5546).
 * Shows tier-specific action options (Sell/Loan for First Team,
 * Promote/Loan/Sell for Academy).
 * @module features/squad-planning/components/ActionMenu
 */

import { Plus } from "lucide-react-native";
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";
import MobileActionMenu from "@/components/molecules/MobileActionMenu";

import type { PlayerAction } from "@/features/squad-planning/types";

/** An extra menu item beyond scenario actions */
export interface ExtraMenuItem {
  readonly key: string;
  readonly label: string;
  /**
   * Whether this item renders with destructive styling. Defaults to `true`
   * (the original "Remove from Academy" shape) so existing callers are
   * unaffected — SE-8409's "Add Annual Salary" is the first item that opts
   * out.
   */
  readonly destructive?: boolean;
}

/**
 * How the desktop trigger presents itself at rest.
 * - `ghost` — revealed by compatible host interaction styling or keyboard focus
 *   (SE-5344 table rows).
 * - `always` — permanently visible (SE-6574 player cards, whose ancestor chain
 *   lacks the parent interaction styling a ghost trigger would need to ever
 *   be revealed).
 */
export type ActionMenuTriggerVisibility = "ghost" | "always";

/**
 * Trigger chrome per visibility mode. Browser-only styles are isolated because
 * a native tablet ≥768px renders this same desktop branch and must not
 * inherit web-only affordances.
 */
const TRIGGER_CLASS_BY_VISIBILITY: Record<ActionMenuTriggerVisibility, string> =
  {
    ghost:
      "web:opacity-0 web:transition-opacity web:focus-visible:opacity-100 web:group-hover:opacity-100",
    // An always-visible trigger cannot signal focus by fading in, so it carries
    // a real focus ring instead (SE-6574 AC5).
    always:
      "web:rounded-sm web:focus-visible:outline-none web:focus-visible:ring-2 web:focus-visible:ring-indicator-primary",
  };

/** Props for {@link ActionMenuView} */
export interface ActionMenuViewProps {
  /** Available actions for this player's tier */
  readonly actions: readonly PlayerAction[];
  /** Pre-built press handlers keyed by action or extra item key */
  readonly actionPressHandlers: Readonly<Record<string, () => void>>;
  /** Additional menu items after the scenario actions (e.g. "Remove from Academy") */
  readonly extraMenuItems?: readonly ExtraMenuItem[];
  /** Whether the mobile action sheet pattern should be used */
  readonly isMobile?: boolean;
  /** Whether the action sheet is open (mobile only) */
  readonly isActionSheetOpen?: boolean;
  /** Called to close the action sheet (mobile only) */
  readonly onCloseActionSheet?: () => void;
  /** Whether the desktop dropdown is open (SE-5570, desktop only) */
  readonly isMenuOpen?: boolean;
  /** Toggle the desktop dropdown (desktop only) */
  readonly onToggleMenu?: () => void;
  /** Close the desktop dropdown (desktop only) */
  readonly onCloseMenu?: () => void;
  /**
   * Whether the desktop trigger is revealed on hover/focus or always shown.
   * Defaults to `ghost` so table rows keep the SE-5344 affordance.
   */
  readonly triggerVisibility?: ActionMenuTriggerVisibility;
}

/** No-op fallback for onClose when not provided */
const NOOP = () => {};

/**
 * Builds the shared desktop dropdown items: scenario actions first, then the
 * destructive extras. Module-level for stability.
 * @param actions - Tier-specific scenario actions.
 * @param actionPressHandlers - Press handlers keyed by action/extra key.
 * @param extraMenuItems - Destructive extras (e.g. "Remove from Academy").
 * @returns Items in the DesktopDropdownMenu shape.
 */
const toDropdownItems = (
  actions: readonly PlayerAction[],
  actionPressHandlers: Readonly<Record<string, () => void>>,
  extraMenuItems?: readonly ExtraMenuItem[]
): DesktopDropdownMenuItem[] => [
  ...actions.map(action => ({
    id: action,
    label: action,
    onPress: actionPressHandlers[action],
    testID: `action-menu-item-${action}`,
  })),
  ...(extraMenuItems ?? []).map(item => ({
    id: item.key,
    label: item.label,
    destructive: item.destructive ?? true,
    onPress: actionPressHandlers[item.key],
    testID: `action-menu-item-${item.key}`,
  })),
];

/**
 * Renders the action menu for a player row in scenario mode.
 * Uses the shared desktop dropdown on desktop, Actionsheet on mobile.
 * @param props - Component props
 * @param props.actions - Available tier-specific actions
 * @param props.actionPressHandlers - Pre-built press handlers keyed by action
 * @param props.extraMenuItems - Additional menu items after scenario actions
 * @param props.isMobile - Whether to use mobile action sheet pattern
 * @param props.isActionSheetOpen - Whether the action sheet is open
 * @param props.onCloseActionSheet - Close handler for the action sheet
 * @param props.isMenuOpen - Whether the desktop dropdown is open
 * @param props.onToggleMenu - Toggle for the desktop dropdown
 * @param props.onCloseMenu - Close for the desktop dropdown
 * @param props.triggerVisibility - Ghost reveal vs always-visible trigger
 */
const ActionMenuView = ({
  actions,
  actionPressHandlers,
  extraMenuItems,
  isMobile = false,
  isActionSheetOpen = false,
  onCloseActionSheet,
  isMenuOpen = false,
  onToggleMenu,
  onCloseMenu,
  triggerVisibility = "ghost",
}: ActionMenuViewProps) =>
  isMobile ? (
    <MobileActionMenu
      isOpen={isActionSheetOpen}
      onClose={onCloseActionSheet ?? NOOP}
      items={[
        ...actions.map(action => ({
          label: action,
          onPress: actionPressHandlers[action],
          testID: `action-menu-item-${action}`,
        })),
        ...(extraMenuItems ?? []).map(item => ({
          label: item.label,
          destructive: item.destructive ?? true,
          onPress: actionPressHandlers[item.key],
          testID: `action-menu-item-${item.key}`,
        })),
      ]}
    />
  ) : (
    <DesktopDropdownMenu
      isOpen={isMenuOpen}
      onClose={onCloseMenu ?? NOOP}
      onToggle={onToggleMenu ?? NOOP}
      triggerTestID="add-action-trigger"
      triggerAccessibilityLabel="Add action"
      triggerClassName={TRIGGER_CLASS_BY_VISIBILITY[triggerVisibility]}
      triggerContent={
        <Stack direction="horizontal" className="items-center gap-micro">
          <Icon as={Plus} className="text-accent-primary" size="2xs" />
          <Text
            variant="unstyled"
            className="font-medium text-caption text-accent-primary"
          >
            Add action
          </Text>
        </Stack>
      }
      items={toDropdownItems(actions, actionPressHandlers, extraMenuItems)}
    />
  );

ActionMenuView.displayName = "ActionMenuView";

export default memo(ActionMenuView);
